import { isNil } from 'lodash';
import React from 'react';
import './BankStatus.scss';
import config from '../../../config/config';
import { BANK_STATUS } from '../../../settings/settings';
import If from '../../Shared/If/If';
import ToolTip from '../../Shared/ToolTip/ToolTip';
import { calculateAndFormatUnixDates } from '../../../utils/dates/date-utility';
import { isRecurringBuyEdit } from '../../../utils/transactions-util';
import { TOOLTIP_POSITION_BOTTOM } from '../../../utils/dom-utils';
import helpIcon from 'src/assets/images/scs-icons/help-icon-bold.svg';

const BankStatus = ({
  accountNumber,
  bank,
  bankStatus,
  bankNotAssociatedEdit,
  bankNotAssociableEdit,
}) =>
  !bankStatus && !isRecurringBuyEdit() ? null : (
    <div className="bank-status">
      <If test={bankStatus === BANK_STATUS.JUST_ADDED}>
        <div className="bank-status-just-added"> Just Added </div>
      </If>
      <If test={bankStatus === BANK_STATUS.CHASE_BANK}>
        <div
          id={`micro-dep-processing-${accountNumber}`}
          className="bank-status-micro-deposits"
        >
          <ToolTip
            callerText="Wire Transfer Unavailable"
            header="Wire Transfer Unavailable"
            message="To redeem funds to this bank, please elect direct deposit."
            placement={TOOLTIP_POSITION_BOTTOM}
          />
        </div>
      </If>
      <If test={isRecurringBuyEdit() && bankNotAssociatedEdit}>
        <div
          id={`not-associated-${accountNumber}`}
          className="bank-status-not-associated"
        >
          This bank is not associated to the selected fund.{' '}
          <a
            className="bank-status-bank-link"
            href={`${config.url}/usis/my-accounts/profile/linked-bank-accounts`}
          >
            Link Bank
          </a>
        </div>
      </If>
      <If test={isRecurringBuyEdit() && bankNotAssociableEdit}>
        <div
          id={`not-associable-${accountNumber}`}
          className="bank-status-not-associated"
        >
          This bank is not associated to the selected fund.{' '}
          <a
            className="bank-status-bank-link college-branded-link"
            href={
              'https://individual.troweprice.com/Retail/Shared/PDFs/shrhldrsrvcs.pdf?WTAFeaturedResult=account%20services%20form'
            }
            rel="noopener noreferrer"
            target="_blank"
          >
            Download Form
          </a>
        </div>
      </If>
      <If test={bankStatus === BANK_STATUS.PREPARING_MICRO_DEPOSITS}>
        <div
          id={`micro-dep-processing-${accountNumber}`}
          className="bank-status-micro-deposits"
        >
          <ToolTip
            callerText={
              <span>
                <span className="bank-status-deposits-icon-text">
                  Preparing Micro-deposits
                </span>
                <img
                  src={helpIcon}
                  alt="help"
                  className="bank-status-deposits-icon"
                />
              </span>
            }
            showBottomBorder={false}
            header="Preparing Micro-deposits"
            message="An email will be sent to you with a link to confirm your micro-deposits soon. Typically takes 2-3 days from initiation date."
            placement={TOOLTIP_POSITION_BOTTOM}
          />
        </div>
      </If>
      <If test={bankStatus === BANK_STATUS.VERIFY_MICRO_DEPOSITS}>
        <a
          id={`micro-verify-dep-${accountNumber}`}
          className="bank-status-verify-micro-deposits"
          href={`${config.url}/usis/my-accounts/profile/linked-bank-accounts`}
        >
          Verify Micro-deposits
        </a>
      </If>
      <If test={bankStatus === BANK_STATUS.PROCESSING}>
        <div
          id={`processing-${accountNumber}`}
          className="bank-status-micro-deposits"
        >
          <ToolTip
            callerText={`Available ${
              !isNil(bank.availbeForRedemtionDate) &&
              calculateAndFormatUnixDates(bank.availbeForRedemtionDate)
            }`}
            header="5-Day Waiting Period"
            message="To protect your investments, please note that during the 5 calendar days following a newly added bank, proceeds from a redemption submitted online can only be sent by a check or to a previously linked bank account."
            placement={TOOLTIP_POSITION_BOTTOM}
          />
        </div>
      </If>
      <If test={bankStatus === BANK_STATUS.REQUIRES_RENEWAL}>
        <div
          id={`processing-${accountNumber}`}
          className="bank-status-micro-deposits"
        >
          <ToolTip
            callerText={
              <span>
                <span className="bank-status-deposits-icon-text">
                  Requires Renewal
                </span>
                <img
                  src={helpIcon}
                  alt="help"
                  className="bank-status-deposits-icon"
                />
              </span>
            }
            showBottomBorder={false}
            header="Requires Renewal"
            message={
              <span>
                The secure connection to this bank account must be reestablished
                before using this bank account for transactions. To use this
                bank for future transactions,{' '}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${config.url}/personal-investing/help/contact/index.html`}
                >
                  contact us
                </a>
                .
              </span>
            }
            placement={TOOLTIP_POSITION_BOTTOM}
          />
        </div>
      </If>
      <If test={bankStatus === BANK_STATUS.DELETED}>
        <div
          id={`processing-${accountNumber}`}
          className="bank-status-micro-deposits"
        >
          <ToolTip
            callerText="Bank Deleted"
            header="Bank Deleted"
            message={
              <span>
                This bank was previously removed from your account. To use a
                bank for this and future transactions,{' '}
                <a
                  href={`${config.url}/personal-investing/help/contact/index.html`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  contact us
                </a>{' '}
                .
              </span>
            }
            placement={TOOLTIP_POSITION_BOTTOM}
          />
        </div>
      </If>
      <If
        test={
          bankStatus === BANK_STATUS.CORRUPT_BANK_DATA ||
          bankStatus === BANK_STATUS.REQUIRES_UPDATE_955
        }
      >
        <div
          id={`processing-${accountNumber}`}
          className="bank-status-micro-deposits"
        >
          <ToolTip
            callerText={
              <span>
                <span className="bank-status-deposits-icon-text">
                  Requires Update
                </span>
                <img
                  src={helpIcon}
                  alt="help"
                  className="bank-status-deposits-icon"
                />
              </span>
            }
            showBottomBorder={false}
            header="Requires Update"
            message={
              <span>
                The secure connection to your bank account requires an update.
                Please{' '}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${config.url}/usis/my-accounts/profile/linked-bank-accounts`}
                >
                  delete and link
                </a>{' '}
                this bank account again or visit the{' '}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${config.url}/personal-investing/help/contact-us.html`}
                >
                  contact us
                </a>{' '}
                page for help reaching an associate.
              </span>
            }
            placement={TOOLTIP_POSITION_BOTTOM}
          />
        </div>
      </If>
      <If test={bankStatus === BANK_STATUS.LAPSED}>
        <div
          id={`lapsed-${accountNumber}`}
          className="bank-status-micro-deposits"
        >
          <ToolTip
            callerText={
              <span>
                <span className="bank-status-deposits-icon-text">
                  Requires Renewal
                </span>
                <img
                  src={helpIcon}
                  alt="help"
                  className="bank-status-deposits-icon"
                />
              </span>
            }
            showBottomBorder={false}
            header="Requires Renewal"
            message={
              <div>
                The secure connection to your bank account must be reestablished
                before using this bank account. Please delete and link this bank
                account again or visit the{' '}
                <a
                  id="lb-contact-link"
                  className="text-nowrap e2e-option-link"
                  href={`${config.url}/personal-investing/help/contact-us.html`}
                  style={{ textDecoration: 'underline' }}
                >
                  contact us
                </a>{' '}
                page for help reaching an associate.
              </div>
            }
            placement={TOOLTIP_POSITION_BOTTOM}
          />
        </div>
      </If>
    </div>
  );

export default BankStatus;
